import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';

import { inject, Injectable } from '@angular/core';
import {
  ErrorResponse,
  ResetPasswordRequest,
  Tenant,
  TenantInfo,
  User,
} from '@verify/models';
import {
  CollectionName,
  FunctionName,
} from '@verify/shared-components/helpers';
import {
  Auth,
  AuthCredential,
  AuthProvider,
  confirmPasswordReset,
  fetchSignInMethodsForEmail,
  User as FireUser,
  getRedirectResult,
  linkWithCredential,
  PopupRedirectResolver,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  updateEmail,
  updatePassword,
  UserCredential,
  verifyPasswordResetCode,
} from 'firebase/auth';

import { Router } from '@angular/router';
import { ChangeNotifierService } from './change-notifier.service';
import { FIREBASE_AUTH } from './firebase.tokens';
import { FirestoreService } from './firestore.service';
import { FunctionService } from './function.service';

export const rootUser = 'ROOTUSER';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private auth: Auth = inject(FIREBASE_AUTH);
  private router = inject(Router);
  private firestoreService: FirestoreService = inject(FirestoreService);
  private functionService: FunctionService = inject(FunctionService);
  private changeNotifier = inject(ChangeNotifierService);

  private static defaultTenant = 'dev1';

  private readonly _currentUser: BehaviorSubject<User | undefined> =
    new BehaviorSubject<User | undefined>(undefined);
  readonly currentUser$ = this._currentUser.asObservable();

  private readonly _tenant: BehaviorSubject<Tenant | undefined> =
    new BehaviorSubject<Tenant | undefined>(undefined);

  private readonly _tenantInfo: BehaviorSubject<TenantInfo | undefined> =
    new BehaviorSubject<TenantInfo | undefined>(undefined);

  readonly tenant$ = this._tenant.asObservable();
  readonly tenantInfo$ = this._tenantInfo.asObservable();

  private unsubscribeAuthState: (() => void) | undefined;

  constructor() {}

  initAuth(tenantName?: string): void {
    if (!this.auth.tenantId || tenantName) {
      if (tenantName) {
        this._tenantInfo.next(undefined);
        this._tenant.next(undefined);
        this.auth.tenantId = null;
      }
      this.getTenantInfo(tenantName || this.getTenantName());
    }
  }

  get currentUser(): User | undefined {
    return this._currentUser.value;
  }

  get tenant(): Tenant {
    return this._tenant.value;
  }

  get tenantInfo(): TenantInfo {
    return this._tenantInfo.value;
  }

  get tenantId(): string {
    return this.auth?.tenantId;
  }

  get isRootUser(): boolean {
    return this._tenant.value?.name === rootUser;
  }

  getAuth(): Auth {
    return this.auth;
  }

  getTenantName(): string {
    if (window.location.host.endsWith('.web.app')) {
      return rootUser;
    }
    const hostParts = window.location.host.split('.');
    return hostParts.length > 1
      ? hostParts[0].padEnd(4, '-')
      : AuthService.defaultTenant;
  }

  updateEmail(newEmail: string): Promise<void> {
    return this.changeNotifier.notifyWhenSettled(
      updateEmail(this.auth.currentUser, newEmail),
    );
  }

  updatePassword(newPassword: string): Promise<void> {
    return this.changeNotifier.notifyWhenSettled(
      updatePassword(this.auth.currentUser, newPassword),
    );
  }

  getRedirectResult(): Promise<UserCredential | null> {
    return this.changeNotifier.notifyWhenSettled(getRedirectResult(this.auth));
  }

  signInWithEmailAndPassword(
    email: string,
    password: string,
  ): Promise<UserCredential> {
    return this.changeNotifier.notifyWhenSettled(
      signInWithEmailAndPassword(this.auth, email, password),
    );
  }

  sendPasswordResetEmail(email: string): Observable<true | ErrorResponse> {
    return this.functionService.call<
      ResetPasswordRequest,
      true | ErrorResponse
    >(FunctionName.resetPassword, {
      tenantId: this.tenantId,
      email,
      application: 'CONSENT_MANAGER',
    });
  }

  verifyPasswordResetCode(oobCode: string): Promise<string> {
    return this.changeNotifier.notifyWhenSettled(
      verifyPasswordResetCode(this.auth, oobCode),
    );
  }

  confirmPasswordReset(oobCode: string, password: string): Promise<void> {
    return this.changeNotifier.notifyWhenSettled(
      confirmPasswordReset(this.auth, oobCode, password),
    );
  }

  fetchSignInMethodsForEmail(email: string): Promise<string[]> {
    return this.changeNotifier.notifyWhenSettled(
      fetchSignInMethodsForEmail(this.auth, email),
    );
  }

  linkWithCredential(
    user: FireUser,
    credential: AuthCredential,
  ): Promise<UserCredential> {
    return this.changeNotifier.notifyWhenSettled(
      linkWithCredential(user, credential),
    );
  }

  signInWithRedirect(
    provider: AuthProvider,
    resolver?: PopupRedirectResolver,
  ): Promise<void> {
    return this.changeNotifier.notifyWhenSettled(
      signInWithRedirect(this.auth, provider, resolver),
    );
  }

  signInWithPopup(
    provider: AuthProvider,
    resolver?: PopupRedirectResolver,
  ): Promise<UserCredential> {
    return this.changeNotifier.notifyWhenSettled(
      signInWithPopup(this.auth, provider, resolver),
    );
  }

  signInWithCustomToken(customToken: string): Promise<UserCredential> {
    return this.changeNotifier.notifyWhenSettled(
      signInWithCustomToken(this.auth, customToken),
    );
  }

  completeAddonSession(sessionId: string): Promise<void> {
    return firstValueFrom(
      this.functionService.call<{ sessionId: string }, void>(
        FunctionName.completeAddonSession,
        { sessionId },
      ),
    );
  }

  private findUser(userUid: string): void {
    this.firestoreService
      .getDocument<User>(`${CollectionName.users}/${userUid}`)
      .subscribe((user) => {
        this._currentUser.next(user || undefined);
      });
  }

  private getTenantInfo(tenantName: string): void {
    if (tenantName === rootUser) {
      console.log(`Tenant set to ${rootUser}`);
      this._tenant.next({ name: rootUser } as Tenant);
      this._tenantInfo.next({
        tenantId: rootUser,
        features: [],
        name: rootUser,
      });
    } else {
      this.functionService
        .call<
          { tenantName: string },
          TenantInfo
        >(FunctionName.getTenantInfo, { tenantName })
        .subscribe((tenantInfo) => {
          if (!tenantInfo) {
            this._tenantInfo.error(new Error(`Tenant ${tenantName} not found`));
            return;
          }
          const tenantId = tenantInfo.tenantId;
          this.auth.tenantId = tenantId;
          this._tenantInfo.next(tenantInfo || undefined);
          console.log(`Tenant set to ${tenantId}`);

          this.unsubscribeAuthState?.();
          this.unsubscribeAuthState = this.auth.onAuthStateChanged(
            (user: FireUser | null) => {
              if (user) {
                user.getIdToken(true);
                if (user.tenantId !== tenantId) {
                  this.auth.signOut().then(() => {
                    this.router.navigateByUrl('/login');
                  });
                } else {
                  this.firestoreService
                    .getDocument<Tenant>('')
                    .subscribe((tenant) => {
                      this._tenant.next(tenant || undefined);
                    });
                  this.findUser(user.uid);
                }
              }
            },
          );
        });
    }
  }
}
